import React, { useState, useEffect } from 'react';
import { Shield, Settings as SettingsIcon, Save, AlertCircle, CheckCircle, QrCode, CheckSquare } from 'lucide-react';
import axios from 'axios';

axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
// Set authorization header dynamically
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

const Settings = () => {
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: 24,
    minPasswordLength: 8,
    maxLoginAttempts: 5,
    passwordExpiry: 90
  });

  const [systemSettings, setSystemSettings] = useState({
    systemName: 'نظام فحص المقبلين على الزواج',
    supportEmail: 'support@prenup.com',
    maxFileSize: 10,
    allowedFileTypes: 'pdf,jpg,png,doc,docx'
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    systemAlerts: true,
    dailyReports: true
  });

  const [qrCodeSettings, setQrCodeSettings] = useState({
    qrCodeUrl: ''
  });

  const [autoApproveSettings, setAutoApproveSettings] = useState({
    enabled: false
  });

  const [loading, setLoading] = useState({
    security: false,
    system: false,
    notifications: false,
    qrCode: false,
    autoApprove: false
  });

  const [messages, setMessages] = useState({
    security: '',
    system: '',
    notifications: '',
    qrCode: '',
    autoApprove: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load QR code settings
      const qrResponse = await axios.get('/admin/settings/qr-code');
      if (qrResponse.data.success) {
        setQrCodeSettings({ qrCodeUrl: qrResponse.data.data.qrCodeUrl || '' });
      }
      
      // Load auto-approve settings
      const autoApproveResponse = await axios.get('/admin/settings/auto-approve');
      if (autoApproveResponse.data.success) {
        setAutoApproveSettings({ enabled: autoApproveResponse.data.data.autoApproveEnabled || false });
      }
      
      // Load other settings from API when implemented
      // const response = await axios.get('/admin/settings');
      // setSecuritySettings(response.data.data.security);
      // setSystemSettings(response.data.data.system);
      // setNotificationSettings(response.data.data.notifications);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const showMessage = (type, message, isSuccess = true) => {
    setMessages({ ...messages, [type]: { text: message, success: isSuccess } });
    setTimeout(() => {
      setMessages({ ...messages, [type]: '' });
    }, 3000);
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, security: true });

    try {
      // await axios.put('/admin/settings/security', securitySettings);
      showMessage('security', 'تم حفظ إعدادات الأمان بنجاح');
    } catch (error) {
      showMessage('security', 'خطأ في حفظ إعدادات الأمان', false);
    } finally {
      setLoading({ ...loading, security: false });
    }
  };

  const handleSystemSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, system: true });

    try {
      // await axios.put('/admin/settings/system', systemSettings);
      showMessage('system', 'تم حفظ إعدادات النظام بنجاح');
    } catch (error) {
      showMessage('system', 'خطأ في حفظ إعدادات النظام', false);
    } finally {
      setLoading({ ...loading, system: false });
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, notifications: true });

    try {
      // await axios.put('/admin/settings/notifications', notificationSettings);
      showMessage('notifications', 'تم حفظ إعدادات الإشعارات بنجاح');
    } catch (error) {
      showMessage('notifications', 'خطأ في حفظ إعدادات الإشعارات', false);
    } finally {
      setLoading({ ...loading, notifications: false });
    }
  };

  const handleQRCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, qrCode: true });

    try {
      const response = await axios.post('/admin/settings/qr-code', qrCodeSettings);
      if (response.data.success) {
        showMessage('qrCode', 'تم حفظ إعدادات رمز الاستجابة السريعة بنجاح');
      }
    } catch (error) {
      showMessage('qrCode', error.response?.data?.message || 'خطأ في حفظ إعدادات رمز الاستجابة السريعة', false);
    } finally {
      setLoading({ ...loading, qrCode: false });
    }
  };

  const handleAutoApproveSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, autoApprove: true });

    try {
      const response = await axios.post('/admin/settings/auto-approve', { enabled: autoApproveSettings.enabled });
      if (response.data.success) {
        showMessage('autoApprove', response.data.message);
      }
    } catch (error) {
      showMessage('autoApprove', error.response?.data?.message || 'خطأ في حفظ إعدادات الموافقة التلقائية', false);
    } finally {
      setLoading({ ...loading, autoApprove: false });
    }
  };

  const SettingsCard = ({ title, icon: Icon, children, onSubmit, loading: cardLoading, message }) => (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="mr-4">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {children}

          {message && (
            <div className={`rounded-md p-3 ${
              message.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {message.success ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  )}
                </div>
                <div className="mr-3">
                  <p className={`text-sm ${
                    message.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {message.text}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={cardLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {cardLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  جاري الحفظ...
                </div>
              ) : (
                <div className="flex items-center">
                  <Save className="w-4 h-4 ml-2" />
                  حفظ الإعدادات
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const FormField = ({ label, children, description }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {children}
      {description && (
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      )}
    </div>
  );

  const Toggle = ({ checked, onChange, label }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          checked ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">إعدادات النظام</h1>
        <p className="mt-1 text-sm text-gray-600">
          إدارة إعدادات النظام العامة والأمان
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Security Settings */}
        <SettingsCard
          title="إعدادات الأمان"
          icon={Shield}
          onSubmit={handleSecuritySubmit}
          loading={loading.security}
          message={messages.security}
        >
          <div className="grid grid-cols-1 gap-4">
            <FormField
              label="مدة انتهاء الجلسة (بالساعات)"
              description="مدة بقاء المستخدم مسجل الدخول قبل انتهاء الجلسة"
            >
              <input
                type="number"
                min="1"
                max="168"
                value={securitySettings.sessionTimeout}
                onChange={(e) => setSecuritySettings({
                  ...securitySettings,
                  sessionTimeout: parseInt(e.target.value)
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </FormField>

            <FormField
              label="الحد الأدنى لطول كلمة المرور"
              description="أقل عدد أحرف مطلوب في كلمة المرور"
            >
              <input
                type="number"
                min="6"
                max="20"
                value={securitySettings.minPasswordLength}
                onChange={(e) => setSecuritySettings({
                  ...securitySettings,
                  minPasswordLength: parseInt(e.target.value)
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </FormField>

            <FormField
              label="الحد الأقصى لمحاولات تسجيل الدخول"
              description="عدد المحاولات المسموحة قبل حظر الحساب مؤقتاً"
            >
              <input
                type="number"
                min="3"
                max="10"
                value={securitySettings.maxLoginAttempts}
                onChange={(e) => setSecuritySettings({
                  ...securitySettings,
                  maxLoginAttempts: parseInt(e.target.value)
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </FormField>

            <FormField
              label="مدة صلاحية كلمة المرور (بالأيام)"
              description="عدد الأيام قبل طلب تغيير كلمة المرور"
            >
              <input
                type="number"
                min="30"
                max="365"
                value={securitySettings.passwordExpiry}
                onChange={(e) => setSecuritySettings({
                  ...securitySettings,
                  passwordExpiry: parseInt(e.target.value)
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </FormField>
          </div>
        </SettingsCard>

        {/* System Settings */}
        <SettingsCard
          title="إعدادات النظام"
          icon={SettingsIcon}
          onSubmit={handleSystemSubmit}
          loading={loading.system}
          message={messages.system}
        >
          <div className="grid grid-cols-1 gap-4">
            <FormField
              label="اسم النظام"
              description="الاسم الذي يظهر في واجهة النظام"
            >
              <input
                type="text"
                value={systemSettings.systemName}
                onChange={(e) => setSystemSettings({
                  ...systemSettings,
                  systemName: e.target.value
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </FormField>

            <FormField
              label="البريد الإلكتروني للدعم"
              description="البريد المستخدم لتلقي رسائل الدعم"
            >
              <input
                type="email"
                value={systemSettings.supportEmail}
                onChange={(e) => setSystemSettings({
                  ...systemSettings,
                  supportEmail: e.target.value
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </FormField>

            <FormField
              label="الحد الأقصى لحجم الملف (بالميجابايت)"
              description="أقصى حجم للملفات المرفوعة"
            >
              <input
                type="number"
                min="1"
                max="100"
                value={systemSettings.maxFileSize}
                onChange={(e) => setSystemSettings({
                  ...systemSettings,
                  maxFileSize: parseInt(e.target.value)
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </FormField>

            <FormField
              label="أنواع الملفات المسموحة"
              description="امتدادات الملفات المسموح برفعها (مفصولة بفاصلة)"
            >
              <input
                type="text"
                value={systemSettings.allowedFileTypes}
                onChange={(e) => setSystemSettings({
                  ...systemSettings,
                  allowedFileTypes: e.target.value
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                placeholder="pdf,jpg,png,doc,docx"
              />
            </FormField>
          </div>
        </SettingsCard>

        {/* Notification Settings */}
        <div className="lg:col-span-2">
          <SettingsCard
            title="إعدادات الإشعارات"
            icon={AlertCircle}
            onSubmit={handleNotificationSubmit}
            loading={loading.notifications}
            message={messages.notifications}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Toggle
                  checked={notificationSettings.emailNotifications}
                  onChange={(value) => setNotificationSettings({
                    ...notificationSettings,
                    emailNotifications: value
                  })}
                  label="إشعارات البريد الإلكتروني"
                />

                <Toggle
                  checked={notificationSettings.smsNotifications}
                  onChange={(value) => setNotificationSettings({
                    ...notificationSettings,
                    smsNotifications: value
                  })}
                  label="إشعارات الرسائل النصية"
                />
              </div>

              <div className="space-y-4">
                <Toggle
                  checked={notificationSettings.systemAlerts}
                  onChange={(value) => setNotificationSettings({
                    ...notificationSettings,
                    systemAlerts: value
                  })}
                  label="تنبيهات النظام"
                />

                <Toggle
                  checked={notificationSettings.dailyReports}
                  onChange={(value) => setNotificationSettings({
                    ...notificationSettings,
                    dailyReports: value
                  })}
                  label="التقارير اليومية"
                />
              </div>
            </div>
          </SettingsCard>
        </div>

        {/* Auto-Approve Settings */}
        <SettingsCard
          title="إعدادات الموافقة التلقائية"
          icon={CheckSquare}
          onSubmit={handleAutoApproveSubmit}
          loading={loading.autoApprove}
          message={messages.autoApprove}
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-blue-400" />
                </div>
                <div className="mr-3">
                  <h4 className="text-sm font-medium text-blue-800">معلومات مهمة</h4>
                  <p className="mt-1 text-sm text-blue-700">
                    عند تفعيل الموافقة التلقائية، جميع الاستمارات الجديدة ستكون في حالة "جاهز للطباعة" تلقائياً.
                    عند إلغاء التفعيل، الاستمارات الجديدة ستحتاج موافقة يدوية من الإدارة.
                  </p>
                </div>
              </div>
            </div>

            <Toggle
              checked={autoApproveSettings.enabled}
              onChange={(value) => setAutoApproveSettings({
                ...autoApproveSettings,
                enabled: value
              })}
              label="تفعيل الموافقة التلقائية للاستمارات الجديدة"
            />

            <div className="text-sm text-gray-600">
              <strong>الحالة الحالية:</strong> {' '}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                autoApproveSettings.enabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {autoApproveSettings.enabled ? 'الموافقة التلقائية مفعلة' : 'الموافقة اليدوية مطلوبة'}
              </span>
            </div>
          </div>
        </SettingsCard>

        {/* QR Code Settings */}
        <div className="lg:col-span-2">
          <SettingsCard
            title="إعدادات رمز الاستجابة السريعة (QR Code)"
            icon={QrCode}
            onSubmit={handleQRCodeSubmit}
            loading={loading.qrCode}
            message={messages.qrCode}
          >
            <div className="grid grid-cols-1 gap-4">
              <FormField
                label="رابط رمز الاستجابة السريعة"
                description="الرابط الذي سيتم توليد رمز الاستجابة السريعة منه في صفحة طباعة الاستمارة"
              >
                <input
                  type="url"
                  value={qrCodeSettings.qrCodeUrl}
                  onChange={(e) => setQrCodeSettings({
                    ...qrCodeSettings,
                    qrCodeUrl: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-left focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                  dir="ltr"
                />
              </FormField>
              
              {qrCodeSettings.qrCodeUrl && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600 mb-2">معاينة رمز الاستجابة السريعة:</p>
                  <div className="flex justify-center">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCodeSettings.qrCodeUrl)}`}
                      alt="QR Code Preview"
                      className="border border-gray-300 rounded"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    الرابط الحالي: {qrCodeSettings.qrCodeUrl}
                  </p>
                </div>
              )}
            </div>
          </SettingsCard>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">معلومات النظام</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500">إصدار النظام</dt>
              <dd className="mt-1 text-sm text-gray-900">1.0.0</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">آخر تحديث</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date().toLocaleDateString('ar-EG')}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">حالة النظام</dt>
              <dd className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  يعمل بشكل طبيعي
                </span>
              </dd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;